import { ActionBarVariant, DateFormat } from '@equip.health/ui';
import { DateTime } from 'luxon';
import { createElement, ReactNode } from 'react';
import {
  CareTeamProviderType,
  CareTeamProviderTypeFromDisplayName,
  CareTeamProviderTypeNames,
  intakeOnlyMedicalProviderTypeNames,
} from '~/lib/constants/patients.constants';
import { InquiryStatusValue } from '~/lib/constants/inquiry.constants';
import { isoToLocalDateTime } from '~/lib/util/date.util';
import { getExactAgeFromDOB } from '~/lib/util/patient.util';
import {
  ActionBarMessage,
  alternateIntakeAppointmentTypes,
  AVAILABILITY_WINDOW_WEEKS,
  defaultIntakeAppointmentTypes,
  MAX_PROVIDERS_FOR_AVAILABILITY_FETCH,
  MIN_AVAILABILITY_BUFFER_IN_HOURS,
  ProviderAssignmentSelectionTab,
} from './constants';
import { AvatarUser } from './SelectableUserAvatarBlock';
import { datadogRum } from '@datadog/browser-rum';
import { coalesceError } from '~/lib/common/coalesceError';
import { MissingFieldError } from '~/lib/util/api-error.util';

const VALID_AVAILABILITY_PREFERENCES: ReadonlySet<PatientAvailabilityPreference> =
  new Set(['8am-11am', '11am-2pm', '2pm-5pm', '5pm-8pm']);

/**
 * Extracts IANA timezone from malformed timezone strings.
 * Handles formats like "Africa/Cairo Egypt" -> "Africa/Cairo"
 */
export const extractIANATimezone = (timezone?: string): string | undefined => {
  if (!timezone || typeof timezone !== 'string') {
    return undefined;
  }

  const parts = timezone.split(' ');
  if (parts.length > 1 && parts[0].includes('/')) {
    return parts[0];
  }

  return timezone;
};

/**
 * Converts an ISO string to DateTime in a specified timezone.
 * Falls back to browser local timezone when timezone is not provided or invalid.
 */
export const isoToTimezoneDateTime = (
  isoString: string,
  timezone?: string,
): DateTime => {
  const utcDateTime = DateTime.fromISO(isoString, { zone: 'utc' });
  if (!timezone) {
    return utcDateTime.toLocal();
  }
  const result = utcDateTime.setZone(timezone);
  if (!result.isValid) {
    datadogRum.addError(
      new Error(`Invalid timezone in isoToTimezoneDateTime: "${timezone}"`),
      {
        extra: { isoString, timezone, invalidReason: result.invalidReason },
        tags: { logFrom: 'isoToTimezoneDateTime' },
      },
    );
    return utcDateTime.toLocal();
  }
  return result;
};

/**
 * Parses a date-only ISO string (e.g. "2026-08-10") as a calendar date anchored
 * in the given timezone, so it can be safely compared against other timezone-aware
 * DateTimes without introducing a dependency on the system's default zone.
 * Falls back to the system default zone when no timezone is given, or when the
 * given timezone is invalid.
 */
export const parseDateOnlyInTimezone = (
  dateOnlyISO: string,
  timezone?: string,
): DateTime => {
  if (!timezone) return DateTime.fromISO(dateOnlyISO);
  const zoned = DateTime.fromISO(dateOnlyISO, { zone: timezone });
  return zoned.isValid ? zoned : DateTime.fromISO(dateOnlyISO);
};

export const getRelativeDateString = (
  isoString: string,
  timezone?: string,
): string => {
  const isDateOnly = !isoString.includes('T');

  let targetDate: DateTime;
  if (isDateOnly) {
    if (timezone) {
      targetDate = DateTime.fromISO(isoString, { zone: timezone }).startOf(
        'day',
      );
    } else {
      targetDate = DateTime.fromISO(isoString).startOf('day');
    }
  } else {
    targetDate = isoToLocalDateTime(isoString).startOf('day');
  }

  const now = timezone
    ? DateTime.local().setZone(timezone).startOf('day')
    : DateTime.local().startOf('day');

  const daysDiff = Math.round(targetDate.diff(now, 'days').days);

  if (daysDiff === 0) {
    return 'Today';
  } else if (daysDiff === 1) {
    return 'Tomorrow';
  } else if (daysDiff === -1) {
    return '1 day ago';
  } else if (daysDiff < 0) {
    return `${Math.abs(daysDiff)} days ago`;
  } else {
    return `${daysDiff} days away`;
  }
};

const isPatientAvailabilityPreferenceMatch = (
  slot: ProviderAppointmentSlot,
  patientAvailabilityPreferences: PatientAvailabilityPreferences | null,
  timezone?: string,
): boolean => {
  if (!patientAvailabilityPreferences || patientAvailabilityPreferences.length === 0) {
    return false;
  }

  const { hour: startHour } = isoToTimezoneDateTime(slot.startTime, timezone);
  const { hour: endHour } = isoToTimezoneDateTime(slot.endTime, timezone);

  return patientAvailabilityPreferences.some((preference) => {
    switch (preference) {
      case '8am-11am':
        return startHour >= 8 && endHour < 11;
      case '11am-2pm':
        return startHour >= 11 && endHour < 14;
      case '2pm-5pm':
        return startHour >= 14 && endHour < 17;
      case '5pm-8pm':
        return startHour >= 17 && endHour < 20;
      default:
        return false;
    }
  });
};

const formatTime = (date: DateTime, includeMeridiem = false): string =>
  date
    .toFormat(includeMeridiem ? 'h:mma' : 'h:mm')
    .toLowerCase()
    .replace(':00', '');

export const formatAppointmentStartEndTime = (
  startTime: string,
  endTime: string,
  options?: {
    showStartMeridiem?: boolean;
    showEndMeridiem?: boolean;
    timezone?: string;
  },
): string => {
  const startDt = isoToTimezoneDateTime(startTime, options?.timezone);
  const endDt = isoToTimezoneDateTime(endTime, options?.timezone);

  const startMeridiem = startDt.toFormat('a');
  const endMeridiem = endDt.toFormat('a');

  let startFormatted: string;
  let endFormatted: string;

  if (
    options?.showStartMeridiem === undefined &&
    options?.showEndMeridiem === undefined
  ) {
    startFormatted = formatTime(startDt, startMeridiem !== endMeridiem);
    endFormatted = formatTime(endDt, true);
  } else {
    startFormatted = formatTime(startDt, options.showStartMeridiem);
    endFormatted = formatTime(endDt, options.showEndMeridiem);
  }

  return `${startFormatted} - ${endFormatted}`;
};

/**
 * Formats an ISO date string to short format (e.g., "Mon, Jan 15")
 * Converts to the given timezone (falls back to local) so the date shown
 * matches the patient's day, not the viewing staff member's.
 */
export const formatShortDate = (isoString: string, timezone?: string): string =>
  isoToTimezoneDateTime(isoString, timezone).toFormat(
    DateFormat.shortDayMonthDateNoPad,
  );

/**
 * Formats an ISO date string to full weekday + short month + day (e.g., "Monday, Jul 13")
 * Converts to the given timezone (falls back to local) so the date shown
 * matches the patient's day, not the viewing staff member's.
 */
export const formatFullWeekdayDate = (isoString: string, timezone?: string): string =>
  isoToTimezoneDateTime(isoString, timezone).toFormat(
    DateFormat.weekdayShortMonthDate,
  );

/**
 * Formats appointment start/end time with end meridiem shown
 * Wrapper around formatAppointmentStartEndTime with standard options
 */
export const formatAppointmentShortTime = (
  startIso: string,
  endIso: string,
  timezone?: string,
): string => formatAppointmentStartEndTime(startIso, endIso, { timezone });

/**
 * Formats recurrence frequency into a human-readable label
 * @param recurrence - Recurrence frequency (e.g., "Weekly", "Biweekly", "Monthly")
 * @param startTimeISO - Start time to determine day of week
 * @returns Formatted label (e.g., "Every Wednesday") or undefined if not repeating
 */
export const formatRecurrenceLabel = (
  recurrence: string | undefined,
  startTimeISO: string,
): string | undefined => {
  if (!recurrence || recurrence === 'Does not repeat') {
    return undefined;
  }

  const dayOfWeek = DateTime.fromISO(startTimeISO).toFormat('EEEE');

  switch (recurrence) {
    case 'Weekly':
      return `Every ${dayOfWeek}`;
    case 'Biweekly':
      return `Every other ${dayOfWeek}`;
    case 'Monthly':
      return `Monthly on ${dayOfWeek}`;
    default:
      return undefined;
  }
};

const sortSlots = (slots: AppointmentSlot[]): AppointmentSlot[] => {
  return [...slots].sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
  );
};

/** Group slots by date and embellish for use by the TimeSlotSelection component */
export const getGroupedSlotsByDate = ({
  slots,
  providerMap,
  patientAvailabilityPreferences,
  patientTimezone,
}: {
  slots: ProviderAppointmentSlot[];
  providerMap: Record<string, GetProviderByIdResponse>;
  patientAvailabilityPreferences: PatientAvailabilityPreferences | null;
  patientTimezone?: string;
}): Record<string, AppointmentSlot[]> => {
  if (!providerMap || !slots) return {};
  const grouped = slots.reduce<Record<string, AppointmentSlot[]>>(
    (acc, slot) => {
      if (!providerMap[slot.providerId]) return acc;

      const date = isoToTimezoneDateTime(
        slot.startTime,
        patientTimezone,
      ).toISODate();

      acc[date] ??= [];

      acc[date].push({
        ...slot,
        formattedStartEndTime: formatAppointmentStartEndTime(
          slot.startTime,
          slot.endTime,
          { timezone: patientTimezone },
        ),
        isPatientAvailabilityPreferenceMatch:
          isPatientAvailabilityPreferenceMatch(
            slot,
            patientAvailabilityPreferences,
            patientTimezone,
          ),
        isSelected: false,
      });

      return acc;
    },
    {},
  );

  // Sort each date group once after all slots have been grouped
  return Object.fromEntries(
    Object.entries(grouped).map(([date, dateSlots]) => [
      date,
      sortSlots(dateSlots),
    ]),
  );
};

export const getDOBString = (dateOfBirth: string): string => {
  try {
    const dobDT = DateTime.fromFormat(dateOfBirth, 'yyyy-MM-dd');
    if (dobDT.isValid) {
      return `${dobDT.toFormat(DateFormat.shortMonthDayYear)} (${getExactAgeFromDOB(dateOfBirth, 'yyyy-MM-dd', 'long')})`;
    }
    throw new Error(`Invalid date: ${dateOfBirth}`);
  } catch (e) {
    datadogRum.addError(coalesceError(e), {
      extra: { message: 'Error parsing date of birth', dateOfBirth },
      tags: { logFrom: 'getDOBString' },
    });
    return null;
  }
};

export const getLocation = (state: string, timezone: string): string => {
  const stateTrimmed = state?.trim();
  const timezoneTrimmed = timezone?.trim();
  if (!stateTrimmed) return null;
  if (!timezoneTrimmed) return stateTrimmed;
  return `${stateTrimmed} (${timezoneTrimmed})`;
};

export const getItemsFromList = (list: string[]): string => {
  if (!list?.length) return null;
  return list.join(', ');
};

export const getTieredProviders = (
  providerAssignmentsData: GetProviderAssignmentRecommendationsResponse,
  providerMap: Record<string, GetProviderByIdResponse>,
  providerType: CareTeamProviderType,
  tier: 1 | 2 | 3,
) => {
  const { assignmentOptions } = providerAssignmentsData ?? {};

  if (!providerMap || !providerType || !assignmentOptions?.length) return [];

  const displayName = CareTeamProviderTypeNames[providerType];

  const { tiers } =
    assignmentOptions.find(
      (option) =>
        option.providerType.toLowerCase() === displayName.toLowerCase(),
    ) ?? {};

  const { providers } = tiers?.find((t) => t.tier === tier) ?? {};

  return (providers ?? []).map((p) => providerMap[p.userId]).filter(Boolean);
};

const convertProviderListToMap = (
  providers: GetProviderByIdResponse[],
): Record<string, GetProviderByIdResponse> =>
  Object.fromEntries(providers.map((provider) => [provider.userId, provider]));

export const getTieredProviderMap = (
  providerAssignmentsData: GetProviderAssignmentRecommendationsResponse,
  providerMap: Record<string, GetProviderByIdResponse>,
  providerType: CareTeamProviderType,
): Record<
  ProviderAssignmentSelectionTab,
  Record<string, GetProviderByIdResponse>
> => ({
  [ProviderAssignmentSelectionTab.STRONG]: convertProviderListToMap(
    getTieredProviders(providerAssignmentsData, providerMap, providerType, 1),
  ),
  [ProviderAssignmentSelectionTab.REASONABLE]: convertProviderListToMap(
    getTieredProviders(providerAssignmentsData, providerMap, providerType, 2),
  ),
  [ProviderAssignmentSelectionTab.LOW]: convertProviderListToMap(
    getTieredProviders(providerAssignmentsData, providerMap, providerType, 3),
  ),
});

export function extractPatientAvailabilityPreferences(
  intakeAssessment: SurveyResponse | null,
): PatientAvailabilityPreferences | null {
  if (!intakeAssessment?.jsonResults) return null;
  try {
    const parsed = JSON.parse(intakeAssessment.jsonResults);
    if (
      parsed.intake_sch_preference_presence &&
      Array.isArray(parsed.intake_sch_preferences) &&
      parsed.intake_sch_preferences.length > 0
    ) {
      const filtered = parsed.intake_sch_preferences.filter(
        (pref): pref is PatientAvailabilityPreference =>
          typeof pref === 'string' && VALID_AVAILABILITY_PREFERENCES.has(pref as PatientAvailabilityPreference),
      );
      return filtered.length > 0 ? filtered : null;
    }
  } catch (e) {
    datadogRum.addError(coalesceError(e), {
      extra: { message: 'Failed to parse intakeAssessment.jsonResults' },
      tags: { logFrom: 'extractPatientAvailabilityPreferences' },
    });
  }
  return null;
}

export function buildProviderMapFromGetProviderByIdResponse(
  results: GetProviderByIdResponse[],
): Record<string, GetProviderByIdResponse> {
  if (!Array.isArray(results)) return {};
  return Object.fromEntries(
    (results ?? [])
      .map((provider) => provider?.userId && [provider.userId, provider])
      .filter(Boolean),
  );
}

/**
 * Resolves a provider role name using the preferred precedence:
 * providerTypeDetails.name -> providerTypeName -> explicit fallbackRole.
 */
export const getPreferredProviderRoleName = ({
  fallbackRole,
  provider,
}: {
  fallbackRole: string;
  provider?: Partial<GetProviderByIdResponse> | null;
}): string =>
  provider?.providerTypeDetails?.name ||
  provider?.providerTypeName ||
  fallbackRole;

export function getTreatmentProgramDisplay(
  inquiryDetail: InquiryDetailResponse,
  clinicalProfile: PatientClinicalProfile,
) {
  return (
    clinicalProfile?.programName ??
    inquiryDetail?.targetTreatmentProgram?.description
  );
}

export function getAdmitDate(
  inquiryDetail: InquiryDetailResponse,
  clinicalProfile: PatientClinicalProfile,
) {
  const admitDate = clinicalProfile?.admitDate;
  const targetAdmissionDate = inquiryDetail?.targetAdmissionDate;
  const inquiryStatus = inquiryDetail?.status?.name;

  if (!admitDate && !targetAdmissionDate) {
    return { admitDate: null, admitDateDisplay: null };
  }

  const isEnrolled = inquiryStatus === InquiryStatusValue.ENROLLED;

  let formattedDate: string | null;
  let label: string;

  if (isEnrolled) {
    formattedDate = admitDate ?? targetAdmissionDate;
    label = admitDate ? 'Admit date' : 'Target admit date';
  } else {
    formattedDate = targetAdmissionDate;
    label = 'Target admit date';
  }

  if (!formattedDate) {
    return { admitDate: null, admitDateDisplay: null };
  }

  return {
    admitDate: formattedDate,
    admitDateDisplay: `${label} ${formattedDate}`,
  };
}

export const extractProviderIds = (
  resp: GetProviderAssignmentRecommendationsResponse,
): string[] =>
  resp?.assignmentOptions
    ? Array.from(
      new Set(
        resp.assignmentOptions.flatMap((option) =>
          [...option.tiers]
            .sort((a, b) => a.tier - b.tier)
            .flatMap((tier) => tier.providers.map((p) => p.userId)),
        ),
      ),
    ).slice(0, MAX_PROVIDERS_FOR_AVAILABILITY_FETCH)
    : [];

/**
 * Counts total providers across all tiers in IQ response for specific provider type.
 *
 * @param recommendationsData - IQ API response
 * @param providerType - Provider type to count (allows filtering out secondary provider types when IQ payload includes multiple, e.g. Medical Providers & Medical Assistant)
 * @returns Total provider count for specified type
 */
export const getTotalProviderCount = (
  recommendationsData: GetProviderAssignmentRecommendationsResponse,
  providerType: CareTeamProviderType,
): number => {
  if (!recommendationsData?.assignmentOptions || !providerType) return 0;

  const displayName = CareTeamProviderTypeNames[providerType];
  if (!displayName) return 0;

  const option = recommendationsData.assignmentOptions.find(
    (opt) => opt.providerType.toLowerCase() === displayName.toLowerCase(),
  );

  if (!option) return 0;

  return option.tiers.reduce((total, tier) => total + tier.providers.length, 0);
};

/** Looks up the primaryTag (e.g. "BED") for a provider from the IQ recommendations response. */
export const getProviderPrimaryTag = (
  recommendationsData: GetProviderAssignmentRecommendationsResponse | undefined,
  providerId: string | undefined,
): string | undefined =>
  recommendationsData?.assignmentOptions
    ?.flatMap((option) => option.tiers.flatMap((tier) => tier.providers))
    .find((provider) => provider.userId === providerId)
    ?.matchFactors?.primaryTag;

/**
 * Filters tiered provider map to only include providers with ≥1 available slot.
 *
 * @param tieredProviderMap - Map of providers organized by tier
 * @param availableSlots - Array of available appointment slots
 * @returns Filtered map containing only providers with availability
 */
export const filterProvidersWithAvailability = (
  tieredProviderMap: Record<
    ProviderAssignmentSelectionTab,
    Record<string, GetProviderByIdResponse>
  >,
  availableSlots: ProviderAppointmentSlot[],
): Record<
  ProviderAssignmentSelectionTab,
  Record<string, GetProviderByIdResponse>
> => {
  const providerIdsWithSlots = new Set(
    (availableSlots || []).map((slot) => slot.providerId),
  );

  const filterTier = (providers: Record<string, GetProviderByIdResponse>) =>
    Object.fromEntries(
      Object.entries(providers).filter(([providerId]) =>
        providerIdsWithSlots.has(providerId),
      ),
    );

  return {
    [ProviderAssignmentSelectionTab.STRONG]: filterTier(
      tieredProviderMap[ProviderAssignmentSelectionTab.STRONG],
    ),
    [ProviderAssignmentSelectionTab.REASONABLE]: filterTier(
      tieredProviderMap[ProviderAssignmentSelectionTab.REASONABLE],
    ),
    [ProviderAssignmentSelectionTab.LOW]: filterTier(
      tieredProviderMap[ProviderAssignmentSelectionTab.LOW],
    ),
  };
};

/**
 * Sorts providers within a tier by availability:
 * 1. Providers with slots: earliest slot timestamp, then slot count
 * 2. Top-N providers with zero slots: preserve IQ order
 * 3. Beyond-top-N providers: preserve IQ order
 *
 * @param providers - Array of providers in a single tier
 * @param availableSlots - Array of available appointment slots
 * @param topNProviderIds - Set of provider IDs in top-N (had availability fetched)
 * @returns Sorted array of providers
 */
export const sortProvidersByAvailability = (
  providers: GetProviderByIdResponse[],
  availableSlots: ProviderAppointmentSlot[],
  topNProviderIds: Set<string>,
): GetProviderByIdResponse[] => {
  // Build provider slot metadata
  const providerSlotMap = availableSlots.reduce((map, slot) => {
    const existing = map.get(slot.providerId);
    if (!existing) {
      map.set(slot.providerId, {
        earliest: slot.startTime,
        count: 1,
      });
    } else {
      map.set(slot.providerId, {
        earliest:
          new Date(slot.startTime) < new Date(existing.earliest)
            ? slot.startTime
            : existing.earliest,
        count: existing.count + 1,
      });
    }
    return map;
  }, new Map<string, { earliest: string; count: number }>());

  // Three-tier sort
  const withSlots: GetProviderByIdResponse[] = [];
  const topNNoSlots: GetProviderByIdResponse[] = [];
  const beyondTopN: GetProviderByIdResponse[] = [];

  providers.forEach((provider) => {
    const hasSlots = providerSlotMap.has(provider.userId);
    const isTopN = topNProviderIds.has(provider.userId);

    if (hasSlots) {
      withSlots.push(provider);
    } else if (isTopN) {
      topNNoSlots.push(provider);
    } else {
      beyondTopN.push(provider);
    }
  });

  // Sort group 1 by earliest slot, then count
  withSlots.sort((a, b) => {
    const aData = providerSlotMap.get(a.userId)!;
    const bData = providerSlotMap.get(b.userId)!;

    const aTime = new Date(aData.earliest).getTime();
    const bTime = new Date(bData.earliest).getTime();
    if (aTime !== bTime) {
      return aTime - bTime;
    }
    return bData.count - aData.count; // Descending count
  });

  return [...withSlots, ...topNNoSlots, ...beyondTopN];
};

export const getProviderTypeLabel = (providerType: string): string => {
  if (providerType === CareTeamProviderType.MEDICAL_PROVIDER) {
    return 'Medical Provider (Intake)';
  }
  return CareTeamProviderTypeNames[providerType];
};

export const getProviderType = (provider: GetProviderByIdResponse): string => {
  // Note: GET providers v2 endpoint returns providerTypeDetails.name, not providerTypeName
  const providerTypeName =
    provider.providerTypeDetails?.name || provider.providerTypeName;

  if (intakeOnlyMedicalProviderTypeNames.has(providerTypeName)) {
    return CareTeamProviderType.MEDICAL_PROVIDER;
  }
  return CareTeamProviderTypeFromDisplayName[providerTypeName];
};

export function getCriticalDataMissingActionBarConfig(
  missingFields?: MissingFieldError[],
): {
  text: ReactNode;
  variant: ActionBarVariant;
} {
  if (!missingFields || missingFields.length === 0) {
    return {
      text: 'An error occurred while loading data. Please try again or contact support.',
      variant: 'critical',
    };
  }

  const fieldNames = missingFields.map((f) => f.displayName).join(', ');
  return {
    text: `Please add the following required information to continue: ${fieldNames}`,
    variant: 'critical',
  };
}

export function getSubmitErrorActionBarConfig(errorMessage?: string): {
  text: ReactNode;
  variant: ActionBarVariant;
} {
  return {
    text:
      errorMessage ||
      "We weren't able to schedule this appointment. Please try again or go back and select a different intake time.",
    variant: 'critical',
  };
}

export function getIqErrorActionBarConfig(errorMessage: string): {
  text: ReactNode;
  variant: ActionBarVariant;
} {
  return {
    text: errorMessage,
    variant: 'critical',
  };
}

export function getNoRecommendationsActionBarConfig(
  providerType: CareTeamProviderType,
): {
  text: ReactNode;
  variant: ActionBarVariant;
} {
  const label =
    CareTeamProviderTypeNames[providerType]?.toLowerCase() ?? 'provider';
  return {
    text: `There are no recommended ${label}s available at this time. Proceed to the patient chart to assign a ${label} or contact support.`,
    variant: 'warning',
  };
}

export function getNoAvailabilityActionBarConfig(
  providerType: CareTeamProviderType,
): {
  text: ReactNode;
  variant: ActionBarVariant;
} {
  const label =
    CareTeamProviderTypeNames[providerType]?.toLowerCase() ?? 'provider';
  return {
    text: createElement(
      'span',
      null,
      'There are no intake slots available for the recommended ',
      `${label}s at this time. You may `,
      createElement('b', null, 'Assign without scheduling'),
      ' or contact support.',
    ),
    variant: 'warning',
  };
}

export function getFollowUpWindowExpiredActionBarConfig(): {
  text: ReactNode;
  variant: ActionBarVariant;
} {
  return {
    text: 'Follow-up scheduling is unavailable because the admit date is too far in the past. Update the admit date in the patient profile to enable follow-up scheduling.',
    variant: 'warning',
  };
}

/**
 * @deprecated Use specific action bar config functions instead:
 * - getCriticalDataMissingActionBarConfig()
 * - getSubmitErrorActionBarConfig()
 * - getIqErrorActionBarConfig(errorMessage)
 * - getNoRecommendationsActionBarConfig(providerType)
 * - getNoAvailabilityActionBarConfig(providerType)
 */
export function getActionBarConfig(
  message:
    | ActionBarMessage.CRITICAL_DATA_MISSING
    | ActionBarMessage.SUBMIT_ERROR,
): { text: ReactNode; variant: ActionBarVariant };
export function getActionBarConfig(
  message: ActionBarMessage.IQ_ERROR,
  errorMessage: string,
): { text: ReactNode; variant: ActionBarVariant };
export function getActionBarConfig(
  message:
    | ActionBarMessage.NO_RECOMMENDATIONS
    | ActionBarMessage.NO_AVAILABILITY,
  providerType: CareTeamProviderType,
): { text: ReactNode; variant: ActionBarVariant };
export function getActionBarConfig(
  message: ActionBarMessage,
  labelOrErrorMessage: string | CareTeamProviderType = 'provider',
): { text: ReactNode; variant: ActionBarVariant } {
  switch (message) {
    case ActionBarMessage.CRITICAL_DATA_MISSING:
      return getCriticalDataMissingActionBarConfig();
    case ActionBarMessage.SUBMIT_ERROR:
      return getSubmitErrorActionBarConfig();
    case ActionBarMessage.IQ_ERROR:
      return getIqErrorActionBarConfig(
        typeof labelOrErrorMessage === 'string'
          ? labelOrErrorMessage
          : 'An error occurred while loading provider recommendations. Please try again or contact support.',
      );
    case ActionBarMessage.NO_RECOMMENDATIONS:
      return getNoRecommendationsActionBarConfig(
        typeof labelOrErrorMessage === 'string'
          ? (labelOrErrorMessage as CareTeamProviderType)
          : CareTeamProviderType.THERAPIST,
      );
    case ActionBarMessage.NO_AVAILABILITY:
      return getNoAvailabilityActionBarConfig(
        typeof labelOrErrorMessage === 'string'
          ? (labelOrErrorMessage as CareTeamProviderType)
          : CareTeamProviderType.THERAPIST,
      );
    case ActionBarMessage.FOLLOW_UP_WINDOW_EXPIRED:
      return getFollowUpWindowExpiredActionBarConfig();
    default:
      return getCriticalDataMissingActionBarConfig();
  }
}

export const getTreatmentEpisodeId = (
  treatmentEpisodesData: GetTreatmentEpisodesResponse | undefined,
  inquiryId: string,
): string | null =>
  treatmentEpisodesData?.data?.find((ep) => ep.inquiryId === inquiryId)
    ?.treatmentEpisodeId ?? null;
export const getEnrichedUser = (
  attendee: FamilyAttendeeWithSelection,
  patient?: Patient,
): AvatarUser => {
  const base: AvatarUser = {
    chosenName: attendee.chosenName,
    firstName: attendee.firstName,
    lastName: attendee.lastName,
    role: attendee.userType,
  };

  if (attendee.userType === 'patient' && patient) {
    return {
      ...base,
      pronouns: patient.pronounsOnRecord,
    };
  }

  if (attendee.userType === 'support' && patient?.supports) {
    const support = patient.supports.find(
      (s) =>
        s.externalId === attendee.externalId ||
        s.supportExternalId === attendee.externalId,
    );
    if (support) {
      return {
        ...base,
        relationship: support.relationToPatient,
      };
    }
  }

  return base;
};

export const getAvailabilityQueryStartDate = (admitDate: string) => {
  let startDate = DateTime.utc()
    .plus({ hours: MIN_AVAILABILITY_BUFFER_IN_HOURS })
    .startOf('hour');
  const admitDateTime = DateTime.fromISO(admitDate, { zone: 'utc' }).startOf(
    'day',
  ) as DateTime<true>;
  if (admitDateTime >= startDate) {
    startDate = admitDateTime;
  }
  return startDate.toUTC().toISO();
};

export const getAvailabilityQueryEndDate = (startDate: string) => {
  try {
    return DateTime.fromISO(startDate)
      .plus({ weeks: AVAILABILITY_WINDOW_WEEKS })
      .toUTC()
      .toISO();
  } catch (e) {
    datadogRum.addError(coalesceError(e), {
      extra: {
        message: 'Error calculating availability query end date',
        startDate,
      },
      tags: { logFrom: 'getAvailabilityQueryEndDate' },
    });
    return null;
  }
};

export const getIntakeAppointmentTypeOptions = (
  providerType: CareTeamProviderType,
): Partial<AppointmentType>[] => {
  const defaultIntakeOption = defaultIntakeAppointmentTypes[providerType];

  const otherIntakeOptions =
    alternateIntakeAppointmentTypes[providerType] ?? [];

  const allIntakeOptions = defaultIntakeOption
    ? [defaultIntakeOption, ...otherIntakeOptions]
    : otherIntakeOptions;

  return allIntakeOptions;
};

export const toUtcIsoString = (isoString: string): string =>
  DateTime.fromISO(isoString, { setZone: true })
    .toUTC()
    .toFormat("yyyy-MM-dd'T'HH:mm:ss.SSSZZ");

/**
 * Detects if a proposed appointment slot conflicts with any existing appointments.
 * Returns an array of unique formatted conflict dates (e.g., ["11/8", "12/4"]).
 *
 * A conflict occurs when the proposed appointment time window overlaps with
 * an existing appointment (start < targetEnd && end > targetStart).
 *
 * Uses millisecond comparison to handle ISO strings with different timezone offsets.
 * Deduplicates by date to avoid showing the same date multiple times when multiple
 * appointments conflict on the same day.
 *
 * @param proposedStart - ISO string of proposed appointment start time
 * @param proposedEnd - ISO string of proposed appointment end time
 * @param existingAppointments - Array of existing appointments to check against
 * @returns Array of unique formatted conflict date strings, or empty array if no conflicts
 */
export const getConflictingDates = (
  proposedStart: string | undefined | null,
  proposedEnd: string | undefined | null,
  existingAppointments: AppointmentDetailResponse[],
): string[] => {
  if (!proposedStart || !proposedEnd || !existingAppointments.length) {
    return [];
  }

  const conflictDates = new Set<string>();
  const proposedStartMs = DateTime.fromISO(proposedStart, {
    setZone: true,
  }).toMillis();
  const proposedEndMs = DateTime.fromISO(proposedEnd, {
    setZone: true,
  }).toMillis();

  existingAppointments.forEach((appointment) => {
    const existingStartMs = DateTime.fromISO(
      appointment.appointmentStartDateTime,
      { setZone: true },
    ).toMillis();
    const existingEndMs = DateTime.fromISO(appointment.appointmentEndDateTime, {
      setZone: true,
    }).toMillis();

    // Check for overlap: start < targetEnd && end > targetStart
    const hasOverlap =
      proposedStartMs < existingEndMs && proposedEndMs > existingStartMs;

    if (hasOverlap) {
      // Format the conflicting date as "M/d" (e.g., "11/8")
      const conflictDate = DateTime.fromISO(
        appointment.appointmentStartDateTime,
      ).toFormat('M/d');
      conflictDates.add(conflictDate);
    }
  });

  return Array.from(conflictDates);
};
